import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CheckCircle2,
  XCircle,
  Clock,
  Send
} from 'lucide-react';
import { teams } from '@/config/teams';

interface Submission {
  teamId: string;
  content: string[];
}

interface SubmissionQueueProps {
  submissions: Submission[];
  onValidate: (teamId: string, isValid: boolean) => void;
}

export function SubmissionQueue({ submissions, onValidate }: SubmissionQueueProps) {
  const { t } = useTranslation();

  return (
    <Card className="p-4 h-[calc(100vh-2rem)]">
      <div className="flex flex-col h-full">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Send className="h-5 w-5" />
          {t('game.queue.title')}
        </h2>

        {submissions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2" />
              <p>{t('game.queue.empty')}</p>
            </div>
          </div>
        ) : (
          <ScrollArea className="flex-1">
            <AnimatePresence>
              <div className="space-y-4 pr-4">
                {submissions.map((submission, index) => {
                  const teamConfig = teams.find(t => t.id === submission.teamId);
                  return (
                    <motion.div
                      key={`${submission.teamId}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className="relative"
                    >
                      <Card className="p-4 border-l-4 border-primary">
                        <div className="space-y-4">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">
                                {teamConfig ? t(teamConfig.name) : submission.teamId}
                              </h3>
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">
                                {submission.content.length} {t('game.team.ideas')}
                              </span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-500 hover:text-green-600 hover:bg-green-100"
                                onClick={() => onValidate(submission.teamId, true)}
                              >
                                <CheckCircle2 className="h-5 w-5" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-600 hover:bg-red-100"
                                onClick={() => onValidate(submission.teamId, false)}
                              >
                                <XCircle className="h-5 w-5" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {submission.content.map((idea, ideaIndex) => (
                              <motion.div
                                key={ideaIndex}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: ideaIndex * 0.1 }}
                              >
                                <p className="text-sm">{idea}</p>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          </ScrollArea>
        )}
      </div>
    </Card>
  );
}
